import { useEffect, useState } from 'react'
import { useMediaQuery } from '@mui/material'
import theme from '../../theme'
import { MOBILE_QUERY } from '../../hooks/useIsMobile'
import { usePersistStore } from '../../store/persistentStorage'

// From here the open sidebar leaves the page and a docked chat enough room; below it, it collapses
const SIDEBAR_WIDE_QUERY = theme.breakpoints.up('lg')

interface SidebarOpen {
    isOpen: boolean
    toggle: () => void
}

type Layout = 'phone' | 'narrow' | 'wide'

/**
 * Open by default, so the full logo and the labels are what people see first. It collapses when
 * the user collapses it on a wide screen, which is remembered, or when the window is too narrow for
 * it. On phones it is an overlay drawer instead, closed until asked for. Toggles on a narrow window
 * or a phone hold only until the layout changes.
 */
export function useSidebarOpen(): SidebarOpen {
    const isMobile = useMediaQuery(MOBILE_QUERY, { noSsr: true })
    const isWide = useMediaQuery(SIDEBAR_WIDE_QUERY, { noSsr: true })
    const layout: Layout = isMobile ? 'phone' : isWide ? 'wide' : 'narrow'
    const isCollapsedByUser = usePersistStore((state) => state.sidebarCollapsed)
    const setIsCollapsedByUser = usePersistStore((state) => state.setSidebarCollapsed)
    // A toggle holds for its layout only; reset in an effect since a render-time reset breaks useMediaQuery
    const [toggled, setToggled] = useState<{ layout: Layout; isOpen: boolean } | null>(null)
    // eslint-disable-next-line react-hooks/set-state-in-effect -- see above
    useEffect(() => setToggled(null), [layout])

    const opensByDefault = layout === 'wide' && !isCollapsedByUser
    const isOpen = toggled?.layout === layout ? toggled.isOpen : opensByDefault

    const toggle = () => {
        const next = !isOpen
        setToggled({ layout, isOpen: next })
        // Only a choice made where the sidebar opens by default is a preference: on a narrow window
        // opening it is a peek, and on a phone it is the overlay drawer
        if (layout !== 'wide') return
        setIsCollapsedByUser(!next)
    }

    return { isOpen, toggle }
}
